import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { labelBatchApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Table, Modal, Field } from "../../Components/UI/ui";
import { GATE, parseRoll, stickerCode, fmtDate } from "../../lib/lab";
import StickerPrintModal from "./StickerPrintModal";

// Settings → Label stock (specimens guide §2.1): the rolls of pre-printed
// stickers this lab uses. A scan is only accepted from an active roll.
const LabelStock = () => {
  const { can } = useAuth();
  const { toast } = useApp();
  const admin = can(...GATE.branding);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", first: "", last: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [printing, setPrinting] = useState(null);   // roll whose stickers are being printed

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await labelBatchApi.list(); setRows(res?.data || res || []); }
    catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const derived = f.first && f.last ? parseRoll(f.first, f.last) : null;

  const register = async () => {
    setErr(null);
    if (!f.name.trim()) return setErr("Give the roll a name");
    if (!derived || derived.error) return setErr(derived?.error || "Enter the first and last sticker codes");
    setSaving(true);
    try {
      await labelBatchApi.create({ name: f.name.trim(), prefix: derived.prefix, numberWidth: derived.numberWidth, rangeStart: derived.rangeStart, rangeEnd: derived.rangeEnd, notes: f.notes.trim() });
      toast.success(`Roll registered · ${derived.rangeEnd - derived.rangeStart + 1} stickers`);
      setOpen(false); setF({ name: "", first: "", last: "", notes: "" });
      load();
    } catch (e) { setErr(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (b) => {
    if (b.isActive && !window.confirm(`Deactivate "${b.name}"? Stickers from it will no longer be accepted at collection.`)) return;
    setBusy(b._id);
    try { await labelBatchApi.update(b._id, { isActive: !b.isActive }); toast.success(b.isActive ? "Roll deactivated" : "Roll activated"); load(); }
    catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const low = rows.filter((b) => b.isActive && (b.remaining ?? 0) < 100);
  const columns = [
    { key: "name", title: "Roll", render: (b) => (<><div className="primary">{b.name}</div><div className="sub">{b.notes || ""}</div></>) },
    { key: "range", title: "Codes", render: (b) => <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}>{b.example || stickerCode(b.prefix, b.numberWidth, b.rangeStart)} – {stickerCode(b.prefix, b.numberWidth, b.rangeEnd)}</span> },
    { key: "total", title: "Total", align: "right" },
    { key: "usedCount", title: "Used", align: "right", render: (b) => b.usedCount ?? 0 },
    { key: "remaining", title: "Remaining", align: "right", render: (b) => <b style={{ color: b.isActive && (b.remaining ?? 0) < 100 ? "#d64545" : undefined }}>{b.remaining ?? (b.total - (b.usedCount || 0))}</b> },
    { key: "createdAt", title: "Registered", render: (b) => fmtDate(b.createdAt) },
    { key: "isActive", title: "Status", render: (b) => <Badge kind={b.isActive ? "on" : "off"}>{b.isActive ? "Active" : "Inactive"}</Badge> },
    { key: "a", title: "", render: (b) => (
      <div className="lab-row end" style={{ gap: 4 }}>
        <Button size="xs" variant="navy" onClick={() => setPrinting(b)} title="Print stickers from this roll onto blank label stock">Print stickers</Button>
        {admin && <Button size="xs" loading={busy === b._id} onClick={() => toggle(b)}>{b.isActive ? "Deactivate" : "Activate"}</Button>}
      </div>
    ) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="lab-row between">
        <span className="lab-muted">Rolls of pre-printed barcode stickers. At collection a sticker goes on each tube and is scanned — only codes from an active roll are accepted.</span>
        {admin && <Button variant="primary" onClick={() => { setOpen(true); setErr(null); }}><IoMdAdd /> Register roll</Button>}
      </div>
      {low.length > 0 && <div className="lab-danger-text">Low stock: {low.map((b) => `${b.name} (${b.remaining} left)`).join(", ")} — order and register the next roll.</div>}
      <Table columns={columns} rows={rows} loading={loading} empty="No rolls registered — stickers can't be scanned until one is" />
      <span className="lab-muted" style={{ fontSize: 12 }}>Buy rolls pre-printed, or print them yourself from a registered roll's codes with <b>Print stickers</b>.</span>

      <StickerPrintModal open={!!printing} onClose={() => { setPrinting(null); load(); }} batch={printing} />

      <Modal open={open} onClose={() => setOpen(false)} size="sm" title="Register a sticker roll"
        footer={<>{err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}<Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={register}>Register</Button></>}>
        <Field label="Name"><input className="lab-input w-full" placeholder="Roll March-A" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="lab-grid cols-2">
          <Field label="First sticker on the roll"><input className="lab-input w-full" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} placeholder="DL000001" value={f.first} onChange={(e) => setF({ ...f, first: e.target.value })} /></Field>
          <Field label="Last sticker on the roll"><input className="lab-input w-full" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} placeholder="DL005000" value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} /></Field>
        </div>
        {derived && (derived.error
          ? <span className="lab-danger-text">{derived.error}</span>
          : <span className="lab-muted">Prefix <b>{derived.prefix}</b> · {derived.numberWidth} digits · {derived.rangeEnd - derived.rangeStart + 1} stickers ({stickerCode(derived.prefix, derived.numberWidth, derived.rangeStart)} – {stickerCode(derived.prefix, derived.numberWidth, derived.rangeEnd)})</span>)}
        <Field label="Notes"><input className="lab-input w-full" placeholder="Supplier, box number…" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <div className="lab-muted" style={{ fontSize: 12 }}>Ranges can't be edited later — a typo means registering a new roll and deactivating this one.</div>
      </Modal>
    </div>
  );
};

export default LabelStock;
